// Words for clinical measurements (TGCLOUD-1911): the form, the risk records
// lists, the Clinical Records tiles and the summary PDF. A reading is the
// record's data, so it goes into a sentence only through a placeholder
// (CLAUDE.md, Translation). Pure; the `t` defaults to English.
import { english } from 'src/i18n/language';

// QRISK3's five smoking categories (Hippisley-Cox et al. 2017), in its order.
// The server's list is services/riskMeasurements SMOKING_STATUSES; the test
// binds the two.
export const SMOKING_OPTIONS = [
  { value: 'non-smoker', label: 'Non-smoker' },
  { value: 'ex-smoker', label: 'Ex-smoker' },
  { value: 'light', label: 'Light smoker (1 to 9 cigarettes a day)' },
  { value: 'moderate', label: 'Moderate smoker (10 to 19 cigarettes a day)' },
  { value: 'heavy', label: 'Heavy smoker (20 or more cigarettes a day)' }
];

export const smokingText = (value, t = english) => {
  const option = SMOKING_OPTIONS.find((o) => o.value === value);
  return option ? t(option.label) : '';
};

// Every field's label, with its unit. Rendered through `t` where shown.
export const MEASUREMENT_LABELS = {
  measuredOn: 'Measured on',
  systolicBp: 'Systolic blood pressure (mmHg)',
  diastolicBp: 'Diastolic blood pressure (mmHg)',
  totalCholesterol: 'Total cholesterol (mmol/L)',
  hdlCholesterol: 'HDL cholesterol (mmol/L)',
  cholesterolHdlRatio: 'Total cholesterol to HDL ratio',
  heightCm: 'Height (cm)',
  weightKg: 'Weight (kg)',
  smokingStatus: 'Smoking',
  townsendScore: 'Townsend deprivation score'
};

export const MEASUREMENT_FIELDS = Object.keys(MEASUREMENT_LABELS);

const present = (v) => v !== null && v !== undefined && v !== '';

// Body mass index from height and weight, to one decimal place.
export const bmiOf = (heightCm, weightKg) => {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!present(heightCm) || !present(weightKg) || !(h > 0) || !(w > 0))
    return null;
  const metres = h / 100;
  return Math.round((w / (metres * metres)) * 10) / 10;
};

// The ratio the two cholesterol values give, to two decimal places (the
// server's own rounding, services/riskMeasurements).
export const ratioOf = (totalCholesterol, hdlCholesterol) => {
  const tc = Number(totalCholesterol);
  const hdl = Number(hdlCholesterol);
  if (!present(totalCholesterol) || !present(hdlCholesterol)) return null;
  if (!(tc > 0) || !(hdl > 0) || !(hdl < tc)) return null;
  return Math.round((tc / hdl) * 100) / 100;
};

const oneDecimal = (n) => Number(n).toFixed(1);

/**
 * One line for a measurements record: "BP 132/84 · Cholesterol ratio 4.0 ·
 * BMI 27.2 · Ex-smoker · Townsend −2.4".
 */
export function measurementSummary(m, t = english) {
  if (!m) return '';
  const parts = [];
  if (present(m.systolicBp) && present(m.diastolicBp)) {
    parts.push(
      t('BP {{systolic}}/{{diastolic}}', {
        systolic: m.systolicBp,
        diastolic: m.diastolicBp
      })
    );
  } else if (present(m.systolicBp)) {
    parts.push(t('Systolic BP {{value}}', { value: m.systolicBp }));
  } else if (present(m.diastolicBp)) {
    parts.push(t('Diastolic BP {{value}}', { value: m.diastolicBp }));
  }
  if (present(m.cholesterolHdlRatio)) {
    parts.push(
      t('Cholesterol ratio {{value}}', {
        value: oneDecimal(m.cholesterolHdlRatio)
      })
    );
  } else if (present(m.totalCholesterol)) {
    parts.push(t('Total cholesterol {{value}}', { value: m.totalCholesterol }));
  }
  const bmi = bmiOf(m.heightCm, m.weightKg);
  if (bmi !== null) parts.push(t('BMI {{value}}', { value: oneDecimal(bmi) }));
  else if (present(m.weightKg))
    parts.push(t('Weight {{value}} kg', { value: m.weightKg }));
  if (present(m.smokingStatus)) parts.push(smokingText(m.smokingStatus, t));
  if (present(m.townsendScore))
    parts.push(t('Townsend {{value}}', { value: m.townsendScore }));
  return parts.join(' · ');
}

// Every problem the server's measurement check names
// (services/riskMeasurements PROBLEM_CODES; the test binds the two).
export const MEASUREMENT_PROBLEM_CODES = [
  'MEASURED_ON_REQUIRED',
  'MEASURED_ON_INVALID',
  'MEASURED_ON_FUTURE',
  'NO_READINGS',
  'NOT_A_NUMBER',
  'OUT_OF_RANGE',
  'DIASTOLIC_NOT_BELOW_SYSTOLIC',
  'HDL_NOT_BELOW_TOTAL',
  'RATIO_CONTRADICTS_CHOLESTEROL',
  'SMOKING_STATUS_INVALID'
];

/**
 * The words for one problem the server (or the form's own check) names.
 * @param {{ field: string|null, code: string, min?: number, max?: number }} problem
 */
export function measurementProblemText(problem, t = english) {
  const label = MEASUREMENT_LABELS[problem?.field];
  const field = label ? t(label) : '';
  switch (problem?.code) {
    case 'MEASURED_ON_REQUIRED':
      return t('Enter the date the measurements were taken.');
    case 'MEASURED_ON_INVALID':
      return t('The date the measurements were taken is not a valid date.');
    case 'MEASURED_ON_FUTURE':
      return t('The date the measurements were taken cannot be in the future.');
    case 'NO_READINGS':
      return t('Enter at least one measurement.');
    case 'NOT_A_NUMBER':
      return t('{{field}} must be a number.', { field });
    case 'OUT_OF_RANGE':
      return t('{{field}} must be between {{min}} and {{max}}.', {
        field,
        min: problem.min,
        max: problem.max
      });
    case 'DIASTOLIC_NOT_BELOW_SYSTOLIC':
      return t('The diastolic blood pressure must be lower than the systolic.');
    case 'HDL_NOT_BELOW_TOTAL':
      return t('HDL cholesterol must be lower than total cholesterol.');
    case 'RATIO_CONTRADICTS_CHOLESTEROL':
      return t(
        'The ratio does not match the total and HDL cholesterol entered. Correct one, or leave the ratio blank to have it worked out.'
      );
    case 'SMOKING_STATUS_INVALID':
      return t('Choose a smoking status from the list.');
    default:
      return t('The measurements could not be recorded.');
  }
}
